export interface PageResult<T> {
  items: T[];
  lastPage: number;
}

export interface PagingParams {
  per_page?: number;
  current_page?: number;
}

/** Fetches one page of a listing. `null` means the request failed. */
export type PageFetcher<T> = (paging: PagingParams) => Promise<PageResult<T> | null>;

export interface CollectAllPagesOptions {
  /** Page size for the fallback walk (app.topol.io caps per_page at 100). */
  pageSize?: number;
  /**
   * Guards the fallback walk against a backend reporting a nonsense lastPage.
   * A longer level is cut here and comes back with `truncated: true`.
   */
  maxPages?: number;
}

/**
 * Loads a whole listing level.
 *
 * The contract is "no `per_page` means everything", so the first request
 * carries no paging params. A host that pages anyway answers with
 * `lastPage > 1`; then the level is walked again from page 1 with a large
 * `per_page`, because pages of a different size would not line up with the
 * first response.
 *
 * Any failed request returns `null` rather than a partial list, so the caller
 * keeps its "the listing failed" path instead of acting on half the data. A
 * level longer than `maxPages` is the one exception: it comes back cut, with
 * `truncated: true`, so the caller can tell the user the list is incomplete.
 */
export interface CollectedPages<T> {
  items: T[];
  /** True when the level had more than `maxPages` pages and was cut. */
  truncated: boolean;
}

export async function collectAllPages<T>(
  fetchPage: PageFetcher<T>,
  { pageSize = 100, maxPages = 100 }: CollectAllPagesOptions = {},
): Promise<CollectedPages<T> | null> {
  const unpaged = await fetchPage({});
  if (unpaged === null) return null;
  // A NaN or < 2 lastPage means the host returned the whole level.
  if (!(unpaged.lastPage > 1)) return { items: unpaged.items, truncated: false };

  const first = await fetchPage({ per_page: pageSize, current_page: 1 });
  if (first === null) return null;

  const items = [...first.items];
  const last = Math.min(first.lastPage, maxPages);
  for (let page = 2; page <= last; page++) {
    const next = await fetchPage({ per_page: pageSize, current_page: page });
    if (next === null) return null;
    items.push(...next.items);
  }
  return { items, truncated: first.lastPage > maxPages };
}
